import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Globe, Check } from "lucide-react";
import { SUPPORTED_LANGS } from "../i18n";

/**
 * Global language selector — dropdown accessible from every layout.
 * Uses react-i18next; persists to localStorage via LanguageDetector.
 */
export default function LanguageSwitcher({ compact = false }) {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = SUPPORTED_LANGS.find((l) => l.code === i18n.language) || SUPPORTED_LANGS[0];

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const choose = (code, comingSoon) => {
    if (comingSoon) return;
    i18n.changeLanguage(code);
    try { window.localStorage.setItem("africtal_lang", code); } catch (_) {}
    setOpen(false);
  };

  return (
    <div ref={ref} className="afr-lang-switch" data-testid="lang-switcher" style={{ position: "relative", display: "inline-flex" }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={t("nav.language_switch_aria", "Switch language")}
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid="lang-switcher-btn"
        className="afr-btn afr-btn--ghost afr-btn--sm"
        style={{ padding: compact ? "0.35rem 0.6rem" : undefined, gap: "0.35rem" }}
      >
        <Globe size={14} /> <span aria-hidden="true">{current.flag}</span> {!compact && current.nativeLabel}
      </button>
      {open && (
        <ul
          role="listbox"
          data-testid="lang-switcher-menu"
          style={{
            position: "absolute", top: "calc(100% + 6px)", right: 0, zIndex: 200,
            background: "var(--afr-surface)", border: "1px solid var(--afr-border)",
            borderRadius: 12, padding: "0.4rem", minWidth: 190, boxShadow: "0 12px 28px rgba(15,42,66,0.18)",
            listStyle: "none", margin: 0,
          }}
        >
          {SUPPORTED_LANGS.map((l) => (
            <li key={l.code}>
              <button
                type="button"
                onClick={() => choose(l.code, l.comingSoon)}
                role="option"
                aria-selected={l.code === current.code}
                data-testid={`lang-switcher-opt-${l.code}`}
                disabled={l.comingSoon}
                style={{
                  width: "100%", display: "flex", alignItems: "center", gap: "0.5rem",
                  padding: "0.5rem 0.7rem", border: "none", background: l.code === current.code ? "var(--afr-bg-muted)" : "transparent",
                  borderRadius: 8, cursor: l.comingSoon ? "not-allowed" : "pointer",
                  opacity: l.comingSoon ? 0.55 : 1, fontFamily: "inherit", fontSize: "0.9rem", color: "var(--afr-secondary)",
                  textAlign: "left",
                }}
              >
                <span aria-hidden="true" style={{ fontSize: "1rem" }}>{l.flag}</span>
                <span style={{ flex: 1 }}>{l.nativeLabel}</span>
                {l.comingSoon && <span className="afr-badge" style={{ fontSize: "0.66rem" }}>{t("nav.coming_soon", "soon")}</span>}
                {l.code === current.code && !l.comingSoon && <Check size={14} style={{ color: "var(--afr-primary)" }} />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
